/**
 * TemplateWizardPage.jsx
 *
 * Multi-step wizard for creating Action Item templates from pre-built module presets.
 * Designed to be scalable — new presets are added in config/templateWizardPresets.js
 * and automatically appear as cards in Step 1.
 *
 * Wizard Steps:
 *   1. Choose Preset   — pick a module preset (Asset Service, Router Monitoring, etc.)
 *   2. Customise Fields — toggle fields on/off, reorder, tweak labels
 *   3. Template Details — name, key, priority, icon, colour, etc.
 *   4. Preview & Save   — live preview via TemplateRenderer, then save via API
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import { WIZARD_PRESETS } from '@/config/templateWizardPresets';
import TemplateRenderer from '@/components/ActionItems/TemplateRenderer';
import IconPicker from '@/components/Common/IconPicker';
import '@/styles/TemplateWizardStyles.scss';
import {
    FaMagic, FaArrowRight, FaArrowLeft, FaSave, FaSpinner, FaCheckCircle,
    FaEye, FaCog, FaLayerGroup, FaToggleOn, FaToggleOff,
    FaChevronUp, FaChevronDown, FaTimes, FaPencilAlt, FaInfoCircle,
    FaCheck, FaCheckDouble, FaTimesCircle, FaQuestionCircle
} from 'react-icons/fa';
import * as Sentry from '@sentry/react';

const STEPS = [
    { key: 'preset', label: 'Choose Preset', icon: FaLayerGroup },
    { key: 'fields', label: 'Customise Fields', icon: FaPencilAlt },
    { key: 'details', label: 'Template Details', icon: FaCog },
    { key: 'preview', label: 'Preview & Save', icon: FaEye }
];

/** Navigate back target — SettingsPage with the templateManagement tab active */
const BACK_ROUTE = '/admin/settings';
const BACK_STATE = { tab: 'templateManagement' };

/**
 * Flatten all non-row fields from a preset's sections into a toggleable list.
 * Row fields are expanded into their inner column fields.
 * We track parentRow info so we can re-assemble rows later.
 */
const buildFieldList = (sections) => {
    const list = [];
    sections.forEach((section, sIdx) => {
        section.fields.forEach((field) => {
            if (field.type === 'row' && field.columns) {
                field.columns.forEach(col => {
                    (col.fields || []).forEach(cf => {
                        list.push({
                            ...cf,
                            _sectionIndex: sIdx,
                            _sectionTitle: section.title,
                            _parentRowName: field.name,
                            _parentRowLabel: field.label,
                            enabled: true
                        });
                    });
                });
            } else {
                list.push({
                    ...field,
                    _sectionIndex: sIdx,
                    _sectionTitle: section.title,
                    _parentRowName: null,
                    enabled: true
                });
            }
        });
    });
    return list;
};

/**
 * Re-assemble toggleable field list back into the sections / rows format
 * expected by SchemaJson.
 */
const buildSectionsFromFieldList = (originalSections, fieldList) => {
    const sectionMap = {};
    originalSections.forEach((s, idx) => {
        sectionMap[idx] = { title: s.title, columns: s.columns || 1, fields: [] };
    });

    const addedRows = new Set();

    fieldList.forEach(f => {
        if (!f.enabled) return;
        const sIdx = f._sectionIndex;
        if (!sectionMap[sIdx]) return;

        if (f._parentRowName) {
            if (addedRows.has(`${sIdx}-${f._parentRowName}`)) return;
            addedRows.add(`${sIdx}-${f._parentRowName}`);

            const rowSiblings = fieldList.filter(
                rf => rf.enabled && rf._sectionIndex === sIdx && rf._parentRowName === f._parentRowName
            );
            if (rowSiblings.length === 0) return;

            if (rowSiblings.length === 1) {
                const { _sectionIndex, _sectionTitle, _parentRowName, _parentRowLabel, enabled: _enabled, ...clean } = rowSiblings[0];
                sectionMap[sIdx].fields.push(clean);
            } else {
                const spanTotal = 12;
                const spanEach = Math.floor(spanTotal / rowSiblings.length);
                const cols = rowSiblings.map((rf, i) => {
                    const { _sectionIndex: _si, _sectionTitle: _st, _parentRowName: _prn, _parentRowLabel: _prl, enabled: _en, ...clean } = rf;
                    return {
                        span: i === rowSiblings.length - 1 ? spanTotal - spanEach * (rowSiblings.length - 1) : spanEach,
                        fields: [clean]
                    };
                });
                sectionMap[sIdx].fields.push({
                    type: 'row',
                    name: f._parentRowName,
                    label: f._parentRowLabel || f._parentRowName,
                    columns: cols
                });
            }
        } else {
            const { _sectionIndex, _sectionTitle, _parentRowName, _parentRowLabel, enabled: _enabled, ...clean } = f;
            sectionMap[sIdx].fields.push(clean);
        }
    });

    return Object.values(sectionMap).filter(s => s.fields.length > 0);
};

/**
 * Convert a display name into a safe, URL-friendly template key.
 * e.g. "Asset Service Action Item" → "Asset-Service-Action-Item"
 */
const slugifyKey = (name) =>
    (name || '').trim()
        .replace(/[^a-zA-Z0-9\s-_]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-|-$/g, '');

const TemplateWizardPage = () => {
    const { apiFetch, user } = useAuth();
    const navigate = useNavigate();

    // Wizard state
    const [currentStep, setCurrentStep] = useState(0);
    const [selectedPreset, setSelectedPreset] = useState(null);
    const [fieldList, setFieldList] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [expandedHelp, setExpandedHelp] = useState(null);

    // Template metadata
    const [metadata, setMetadata] = useState({
        templateKey: '',
        displayName: '',
        description: '',
        department: '',
        defaultPriority: 'Medium',
        defaultStatus: 'Open',
        defaultDueDays: null,
        icon: '',
        color: '#3498db',
        tags: ''
    });

    // ── STEP NAVIGATION ────────────────────────────────────────────────────────

    const enabledCount = useMemo(() => fieldList.filter(f => f.enabled).length, [fieldList]);

    const canGoNext = useMemo(() => {
        switch (currentStep) {
            case 0: return !!selectedPreset;
            case 1: return enabledCount > 0;
            case 2: return metadata.displayName.trim().length > 0;
            case 3: return true;
            default: return false;
        }
    }, [currentStep, selectedPreset, enabledCount, metadata]);

    const handleNext = () => {
        if (currentStep < STEPS.length - 1 && canGoNext) {
            setCurrentStep(c => c + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 0) setCurrentStep(c => c - 1);
    };

    // ── STEP 1 — CHOOSE PRESET ────────────────────────────────────────────────

    const handleSelectPreset = (preset) => {
        setSelectedPreset(preset);
        const list = buildFieldList(preset.sections);
        setFieldList(list);

        const m = preset.suggestedMeta || {};
        const derivedName = m.displayNameDefault || preset.displayName;
        setMetadata({
            templateKey: slugifyKey(derivedName),
            displayName: derivedName,
            description: m.descriptionDefault || preset.description,
            department: m.department || 'General',
            defaultPriority: m.defaultPriority || 'Medium',
            defaultStatus: 'Open',
            defaultDueDays: m.defaultDueDays || null,
            icon: m.icon || '',
            color: m.color || '#3498db',
            tags: m.tags || ''
        });

        setCurrentStep(1);
    };

    // ── STEP 2 — CUSTOMISE FIELDS ─────────────────────────────────────────────

    const toggleField = (index) => {
        setFieldList(prev => prev.map((f, i) => i === index ? { ...f, enabled: !f.enabled } : f));
    };

    const updateFieldLabel = (index, newLabel) => {
        setFieldList(prev => prev.map((f, i) => i === index ? { ...f, label: newLabel } : f));
    };

    /**
     * Reorder a field within its section group. Movement is restricted to fields
     * sharing the same _sectionIndex so that the visual order (grouped by section)
     * always matches the persisted order in the schema.
     */
    const moveField = (index, direction) => {
        setFieldList(prev => {
            const currentField = prev[index];
            if (!currentField) return prev;

            const targetIndex = index + direction;
            if (targetIndex < 0 || targetIndex >= prev.length) return prev;

            const targetField = prev[targetIndex];

            // Prevent cross-section swapping — only allow moves within the same section
            if (currentField._sectionIndex !== targetField._sectionIndex) return prev;

            const newList = [...prev];
            [newList[index], newList[targetIndex]] = [newList[targetIndex], newList[index]];
            return newList;
        });
    };

    const handleSelectAll = () => setFieldList(prev => prev.map(f => ({ ...f, enabled: true })));
    const handleDeselectAll = () => setFieldList(prev => prev.map(f => f.required ? f : { ...f, enabled: false }));

    // ── STEP 3 — METADATA ─────────────────────────────────────────────────────

    const updateMeta = (key, value) => {
        setMetadata(prev => {
            const next = { ...prev, [key]: value };
            if (key === 'displayName') {
                next.templateKey = slugifyKey(value);
            }
            return next;
        });
    };

    // ── STEP 4 — PREVIEW & SAVE ───────────────────────────────────────────────

    const previewSchema = useMemo(() => {
        if (!selectedPreset) return { sections: [] };
        return { sections: buildSectionsFromFieldList(selectedPreset.sections, fieldList) };
    }, [selectedPreset, fieldList]);

    const previewTemplate = useMemo(() => ({
        templateKey: metadata.templateKey,
        displayName: metadata.displayName,
        schema: previewSchema
    }), [metadata, previewSchema]);

    const handleSave = useCallback(async () => {
        const derivedKey = slugifyKey(metadata.displayName);
        if (!derivedKey || !metadata.displayName.trim()) {
            toast.error('Display Name is required');
            setCurrentStep(2);
            return;
        }

        setIsSaving(true);
        try {
            const schemaJson = JSON.stringify(previewSchema);
            const payload = {
                templateKey: metadata.templateKey,
                displayName: metadata.displayName,
                description: metadata.description || '',
                department: metadata.department || 'General',
                schemaJson,
                defaultPriority: metadata.defaultPriority,
                defaultStatus: metadata.defaultStatus,
                defaultDueDays: metadata.defaultDueDays,
                icon: metadata.icon || null,
                color: metadata.color || null,
                tags: metadata.tags || null,
                sortOrder: 0,
                userId: user.userID
            };

            const response = await apiFetch('/api/Templates', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();
            if (data.success) {
                toast.success('Template created successfully! Redirecting to Template Management...');
                setTimeout(() => navigate(BACK_ROUTE, { state: BACK_STATE }), 1500);
            } else {
                toast.error(data.message || 'Failed to create template');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error saving template:', error);
            toast.error('Failed to save template: ' + error.message);
        } finally {
            setIsSaving(false);
        }
    }, [metadata, previewSchema, apiFetch, user, navigate]);

    // ── RENDER HELPERS ─────────────────────────────────────────────────────────

    const renderStepIndicator = () => (
        <div className="wizard-steps-indicator">
            {STEPS.map((step, idx) => {
                const StepIcon = step.icon;
                const isActive = idx === currentStep;
                const isCompleted = idx < currentStep;
                return (
                    <div
                        key={step.key}
                        className={`wizard-step-dot ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                        onClick={() => { if (idx < currentStep) setCurrentStep(idx); }}
                    >
                        <div className="dot-circle">
                            {isCompleted ? <FaCheck /> : <StepIcon />}
                        </div>
                        <span className="dot-label">{step.label}</span>
                        {idx < STEPS.length - 1 && <div className="dot-connector" />}
                    </div>
                );
            })}
        </div>
    );

    // ── STEP 1: PRESET CARDS ───────────────────────────────────────────────────

    const renderPresetSelection = () => (
        <div className="wizard-step-content animate-fade-in">
            <div className="step-header">
                <h2><FaLayerGroup /> Choose a Template Preset</h2>
                <p>Select the module this template is for. The wizard will pre-configure the fields that the system auto-populates when creating action items from that module.</p>
            </div>
            <div className="preset-cards">
                {WIZARD_PRESETS.map(preset => {
                    const PresetIcon = preset.icon;
                    const isSelected = selectedPreset?.id === preset.id;
                    const fieldCount = buildFieldList(preset.sections).length;
                    const sectionCount = preset.sections.length;
                    return (
                        <div
                            key={preset.id}
                            className={`preset-card ${isSelected ? 'selected' : ''}`}
                            style={{ '--preset-color': preset.color }}
                            onClick={() => handleSelectPreset(preset)}
                        >
                            <div className="preset-card-icon">
                                <PresetIcon />
                            </div>
                            <div className="preset-card-body">
                                <h3>{preset.displayName}</h3>
                                <p>{preset.description}</p>
                                <div className="preset-card-meta">
                                    <span className="badge">{preset.moduleKey} / {preset.contextKey}</span>
                                    <span className="field-count">{sectionCount} section{sectionCount !== 1 ? 's' : ''} · {fieldCount} field{fieldCount !== 1 ? 's' : ''}</span>
                                </div>
                            </div>
                            {isSelected && (
                                <div className="preset-card-check">
                                    <FaCheckCircle />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            {WIZARD_PRESETS.length === 0 && (
                <div className="empty-state">
                    <FaInfoCircle />
                    <p>No presets available. Contact your administrator to add module presets.</p>
                </div>
            )}
        </div>
    );

    // ── STEP 2: FIELD CUSTOMISATION ────────────────────────────────────────────

    const renderFieldCustomisation = () => {
        const grouped = {};
        fieldList.forEach((f, idx) => {
            const key = f._sectionTitle || 'General';
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push({ ...f, _listIndex: idx });
        });

        return (
            <div className="wizard-step-content animate-fade-in">
                <div className="step-header">
                    <div className="step-header-row">
                        <div>
                            <h2><FaPencilAlt /> Customise Fields</h2>
                            <p>Toggle fields on or off, edit labels, and reorder them. Disabled fields will not appear in the template.</p>
                        </div>
                        <div className="bulk-actions">
                            <button className="bulk-btn" onClick={handleSelectAll} title="Enable all fields">
                                <FaCheckDouble /> All
                            </button>
                            <button className="bulk-btn" onClick={handleDeselectAll} title="Disable all optional fields">
                                <FaTimesCircle /> None
                            </button>
                        </div>
                    </div>
                </div>
                <div className="field-customisation">
                    {Object.entries(grouped).map(([sectionTitle, fields]) => (
                        <div key={sectionTitle} className="field-section-group">
                            <h3 className="field-section-title">{sectionTitle}</h3>
                            <div className="field-list">
                                {fields.map(f => (
                                    <div
                                        key={f._listIndex}
                                        className={`field-item ${f.enabled ? 'enabled' : 'disabled'}`}
                                    >
                                        <div className="field-item-grip">
                                            <button
                                                className="move-btn"
                                                onClick={() => moveField(f._listIndex, -1)}
                                                title="Move up"
                                            >
                                                <FaChevronUp />
                                            </button>
                                            <button
                                                className="move-btn"
                                                onClick={() => moveField(f._listIndex, 1)}
                                                title="Move down"
                                            >
                                                <FaChevronDown />
                                            </button>
                                        </div>
                                        <button
                                            className={`toggle-btn ${f.enabled ? 'on' : 'off'}`}
                                            onClick={() => toggleField(f._listIndex)}
                                            title={f.enabled ? 'Disable field' : 'Enable field'}
                                        >
                                            {f.enabled ? <FaToggleOn /> : <FaToggleOff />}
                                        </button>
                                        <div className="field-item-info">
                                            <input
                                                type="text"
                                                className="field-label-input"
                                                value={f.label}
                                                onChange={(e) => updateFieldLabel(f._listIndex, e.target.value)}
                                                disabled={!f.enabled}
                                            />
                                            <span className="field-name-badge">{f.name}</span>
                                            <span className="field-type-badge">{f.type}</span>
                                            {f.required && <span className="required-badge">Required</span>}
                                            {f._parentRowName && <span className="row-badge">Inline</span>}
                                            {f.helpText && (
                                                <button
                                                    className="help-btn"
                                                    onClick={(e) => { e.stopPropagation(); setExpandedHelp(expandedHelp === f._listIndex ? null : f._listIndex); }}
                                                    title={f.helpText}
                                                >
                                                    <FaQuestionCircle />
                                                </button>
                                            )}
                                        </div>
                                        {expandedHelp === f._listIndex && f.helpText && (
                                            <div className="field-help-text">{f.helpText}</div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="field-summary">
                    <FaInfoCircle />
                    <span>{enabledCount} of {fieldList.length} fields enabled</span>
                </div>
            </div>
        );
    };

    // ── STEP 3: TEMPLATE DETAILS ───────────────────────────────────────────────

    const renderDetails = () => (
        <div className="wizard-step-content animate-fade-in">
            <div className="step-header">
                <h2><FaCog /> Template Details</h2>
                <p>Configure the template metadata. The system key is derived automatically from the display name.</p>
            </div>
            <div className="details-form">
                <div className="form-section">
                    <h3>Identity</h3>
                    <div className="form-field full-width">
                        <label>Display Name <span className="required">*</span></label>
                        <input
                            type="text"
                            value={metadata.displayName}
                            onChange={(e) => updateMeta('displayName', e.target.value)}
                            placeholder="e.g. Asset Service Action Item"
                        />
                    </div>
                    {metadata.displayName.trim() && (
                        <div className="derived-key-preview">
                            <span className="derived-key-label">System Key:</span>
                            <code className="derived-key-value">{metadata.templateKey}</code>
                        </div>
                    )}
                    <div className="form-field full-width">
                        <label>Description</label>
                        <textarea
                            value={metadata.description}
                            onChange={(e) => updateMeta('description', e.target.value)}
                            placeholder="Brief description of this template's purpose..."
                            rows={3}
                        />
                    </div>
                </div>

                <div className="form-section">
                    <h3>Defaults</h3>
                    <div className="form-row three-col">
                        <div className="form-field">
                            <label>Default Priority</label>
                            <select
                                value={metadata.defaultPriority}
                                onChange={(e) => updateMeta('defaultPriority', e.target.value)}
                            >
                                <option value="Low">Low</option>
                                <option value="Medium">Medium</option>
                                <option value="High">High</option>
                                <option value="Critical">Critical</option>
                            </select>
                        </div>
                        <div className="form-field">
                            <label>Default Status</label>
                            <select
                                value={metadata.defaultStatus}
                                onChange={(e) => updateMeta('defaultStatus', e.target.value)}
                            >
                                <option value="Open">Open</option>
                                <option value="In Progress">In Progress</option>
                                <option value="Pending">Pending</option>
                            </select>
                        </div>
                        <div className="form-field">
                            <label>Due Days</label>
                            <input
                                type="number"
                                min="0"
                                value={metadata.defaultDueDays || ''}
                                onChange={(e) => updateMeta('defaultDueDays', e.target.value ? parseInt(e.target.value) : null)}
                                placeholder="e.g. 7"
                            />
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h3>Appearance</h3>
                    <div className="form-row">
                        <div className="form-field">
                            <label>Icon</label>
                            <IconPicker
                                value={metadata.icon || ''}
                                onChange={(icon) => updateMeta('icon', icon)}
                                color={metadata.color || '#3498db'}
                                placeholder="Select an icon..."
                            />
                        </div>
                        <div className="form-field">
                            <label>Colour</label>
                            <div className="color-row">
                                <input
                                    type="color"
                                    value={metadata.color}
                                    onChange={(e) => updateMeta('color', e.target.value)}
                                />
                                <input
                                    type="text"
                                    value={metadata.color}
                                    onChange={(e) => updateMeta('color', e.target.value)}
                                    className="color-text"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="form-field full-width">
                        <label>Tags</label>
                        <input
                            type="text"
                            value={metadata.tags}
                            onChange={(e) => updateMeta('tags', e.target.value)}
                            placeholder="Comma-separated tags e.g. asset,service,maintenance"
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    // ── STEP 4: PREVIEW & SAVE ─────────────────────────────────────────────────

    const renderPreview = () => (
        <div className="wizard-step-content animate-fade-in">
            <div className="step-header">
                <h2><FaEye /> Preview & Save</h2>
                <p>Review how the template will look when users fill in action items. Fields marked "Auto-populated" will be filled automatically by the system.</p>
            </div>

            <div className="preview-meta-summary">
                <div className="meta-chip">
                    <span className="meta-label">Key</span>
                    <span className="meta-value">{metadata.templateKey || '—'}</span>
                </div>
                <div className="meta-chip">
                    <span className="meta-label">Name</span>
                    <span className="meta-value">{metadata.displayName || '—'}</span>
                </div>
                <div className="meta-chip">
                    <span className="meta-label">Priority</span>
                    <span className="meta-value">{metadata.defaultPriority}</span>
                </div>
                <div className="meta-chip">
                    <span className="meta-label">Due</span>
                    <span className="meta-value">{metadata.defaultDueDays ? `${metadata.defaultDueDays} days` : 'None'}</span>
                </div>
                <div className="meta-chip">
                    <span className="meta-label">Fields</span>
                    <span className="meta-value">{enabledCount}</span>
                </div>
            </div>

            <div className="preview-container">
                <h3 className="preview-title">Form Preview</h3>
                <div className="preview-form-wrap">
                    <TemplateRenderer
                        template={previewTemplate}
                        readOnly={false}
                        initialData={{}}
                    />
                </div>
            </div>
        </div>
    );

    // ── MAIN RENDER ────────────────────────────────────────────────────────────

    const stepRenderers = [renderPresetSelection, renderFieldCustomisation, renderDetails, renderPreview];

    return (
        <div className="template-wizard-page">


            {/* Page Header */}
            <div className="wizard-page-header">
                <div className="wizard-page-header-left">
                    <h1><FaMagic /> Template Wizard</h1>
                    <p className="page-description">
                        Create a new Action Item template from a pre-built module preset.
                    </p>
                </div>
                <button
                    className="btn btn-back-link"
                    onClick={() => navigate(BACK_ROUTE, { state: BACK_STATE })}
                    title="Back to Template Management"
                >
                    <FaTimes /> Close Wizard
                </button>
            </div>

            {/* Step Indicator */}
            {renderStepIndicator()}

            {/* Step Content */}
            <div className="wizard-body">
                {stepRenderers[currentStep]()}
            </div>

            {/* Navigation Footer */}
            <div className="wizard-footer">
                <button
                    className="btn btn-secondary"
                    onClick={() => currentStep === 0 ? navigate(BACK_ROUTE, { state: BACK_STATE }) : handleBack()}
                >
                    {currentStep === 0 ? (
                        <><FaTimes /> Cancel</>
                    ) : (
                        <><FaArrowLeft /> Back</>
                    )}
                </button>

                <div className="wizard-footer-step-label">
                    Step {currentStep + 1} of {STEPS.length}
                </div>

                <div className="footer-right">
                    {currentStep < STEPS.length - 1 ? (
                        <button
                            className="btn btn-primary"
                            onClick={handleNext}
                            disabled={!canGoNext}
                        >
                            Next <FaArrowRight />
                        </button>
                    ) : (
                        <button
                            className="btn btn-success"
                            onClick={handleSave}
                            disabled={isSaving}
                        >
                            {isSaving ? (
                                <><FaSpinner className="fa-spin" /> Saving...</>
                            ) : (
                                <><FaSave /> Create Template</>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TemplateWizardPage;
